import { useState } from "react";
import { submitDonation } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Sparkles, ShieldCheck, ArrowRight, Droplet, CheckCircle2 } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const RIPPLE = "https://static.prod-images.emergentagent.com/jobs/7c3bd71d-f3a1-4475-868e-2031ff56ad92/images/0da293f3e2da169b65ef1706c0ba1a148d5f9fb022de9609c007727662d20643.png";

const PRESETS = [
  { amount: 25, label: "A week of drop-in meals", icon: Droplet },
  { amount: 50, label: "A safe shower & dignity kit", icon: Sparkles },
  { amount: 100, label: "A mentor session for a young person", icon: Heart },
  { amount: 250, label: "A crisis night, fully covered", icon: ShieldCheck },
];

const DESIGNATIONS = [
  "Where it's needed most",
  "Crisis accommodation",
  "Food & nutritional support",
  "Laundry & hygiene services",
  "Mental health & casework",
  "Mentorship & life skills",
  "Education & employment pathways",
];

export default function Donate() {
  useReveal();
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [designation, setDesignation] = useState(DESIGNATIONS[0]);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const handlePreset = (val) => {
    setAmount(String(val));
    // smoothly scroll to amount field if needed
  };

  const numericAmount = parseFloat(amount || "0");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Please enter a donation amount.");
      return;
    }
    if (!anonymous && (!form.name || !form.email)) {
      toast.error("Please share your name and email — or choose to give anonymously.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount: numericAmount,
        currency: "USD",
        frequency,
        designation,
        name: anonymous ? null : form.name,
        email: anonymous ? null : form.email,
        message: form.message || null,
        anonymous,
      };
      const res = await submitDonation(payload);
      setDone(res);
      toast.success("Thank you — your intent has been received.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="donate-page">
        <section className="ripple-bg">
          <div className="container-app py-24">
            <div className="max-w-2xl mx-auto text-center reveal">
              <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[#E6F0F2] text-[#2E8B8B]">
                <CheckCircle2 className="h-10 w-10" />
              </span>
              <h1 className="font-heading mt-8 text-4xl sm:text-5xl text-[#0A2540] leading-tight">
                Thank you, sincerely.
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                We've received your intention to give{" "}
                <span className="text-[#0A2540] font-medium">
                  ${done.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {done.frequency === "monthly" ? "every month" : "today"}
                </span>{" "}
                toward {done.designation}.
              </p>
              <p className="mt-3 text-base text-slate-500">
                A member of our team will reach out shortly to complete your contribution with care.
                Your generosity will reach the communities that need it most.
              </p>
              <div className="mt-10 grid sm:grid-cols-3 gap-4 text-left max-w-xl mx-auto">
                <div className="rounded-2xl border border-slate-100 bg-white p-5">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Reference</p>
                  <p className="font-heading mt-1 text-base text-[#0A2540] break-all">
                    #{done.id.slice(0, 8).toUpperCase()}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-white p-5">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Amount</p>
                  <p className="font-heading mt-1 text-base text-[#0A2540]">
                    ${done.amount.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-white p-5">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Designation</p>
                  <p className="font-heading mt-1 text-base text-[#0A2540]">{done.designation}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setDone(null);
                  setAmount("");
                  setForm({ name: "", email: "", message: "" });
                }}
                className="btn-ghost mt-10"
                data-testid="donate-reset"
              >
                Make another contribution <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="donate-page">
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">Donate to CLEAN Water Drop Association</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[76px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                Support our services.
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                Donations directly support our shelter, food, hygiene, mental health, mentorship,
                and education and employment programs. Give what you can — every contribution helps
                us provide steady, dignified support.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-app pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* FORM */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              className="rounded-[2rem] bg-white border border-slate-100 p-8 sm:p-10 shadow-sm space-y-8"
              data-testid="donate-form"
            >
              {/* Open amount */}
              <div>
                <label className="text-xs uppercase tracking-[0.22em] text-slate-500">
                  Your contribution
                </label>
                <div className="mt-4 relative">
                  <span className="absolute left-6 top-1/2 -translate-y-1/2 text-3xl font-heading text-slate-400">$</span>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter any amount"
                    data-testid="donate-amount-input"
                    className="w-full rounded-2xl border-2 border-slate-200 bg-white pl-14 pr-6 py-6 text-3xl font-heading text-center text-[#0A2540] placeholder:text-slate-300 focus:border-[#2E8B8B] focus:outline-none focus:ring-2 focus:ring-[#2E8B8B]/10 transition"
                  />
                </div>
                <p className="mt-3 text-sm text-slate-500 text-center">
                  USD — or choose a suggestion below for inspiration.
                </p>
              </div>

              {/* Preset suggestions */}
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-slate-500">
                  Suggestions
                </p>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {PRESETS.map((p) => {
                    const active = numericAmount === p.amount;
                    return (
                      <button
                        type="button"
                        key={p.amount}
                        onClick={() => handlePreset(p.amount)}
                        data-testid={`preset-${p.amount}`}
                        className={`group rounded-2xl border-2 p-4 text-left transition-all ${
                          active
                            ? "border-[#2E8B8B] bg-[#E6F0F2]/40"
                            : "border-slate-100 hover:border-[#2E8B8B] hover:bg-[#E6F0F2]/30"
                        }`}
                      >
                        <p.icon className={`h-5 w-5 ${active ? "text-[#2E8B8B]" : "text-slate-400 group-hover:text-[#2E8B8B]"} transition-colors`} />
                        <p className="font-heading mt-3 text-xl text-[#0A2540]">${p.amount}</p>
                        <p className="mt-1 text-xs text-slate-500 leading-snug">{p.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Frequency */}
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Frequency</p>
                <div className="mt-3 inline-flex rounded-full border border-slate-200 p-1 bg-white">
                  {[
                    { v: "one-time", l: "One-time" },
                    { v: "monthly", l: "Monthly" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setFrequency(opt.v)}
                      data-testid={`frequency-${opt.v}`}
                      className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                        frequency === opt.v
                          ? "bg-[#0A2540] text-white"
                          : "text-slate-600 hover:text-[#0A2540]"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Designation */}
              <div>
                <label className="text-xs uppercase tracking-[0.22em] text-slate-500">
                  Direct your gift
                </label>
                <select
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  className="input-field mt-3"
                  data-testid="donate-designation"
                >
                  {DESIGNATIONS.map((d) => (
                    <option key={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Personal details */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Name {anonymous && "(hidden)"}</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-field mt-2"
                    placeholder="Your name"
                    disabled={anonymous}
                    data-testid="donate-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-slate-500">Email {anonymous && "(hidden)"}</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input-field mt-2"
                    placeholder="you@email.com"
                    disabled={anonymous}
                    data-testid="donate-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.22em] text-slate-500">A note (optional)</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="input-field mt-2 resize-none"
                  placeholder="A word of intention, a name to honor, or a reason for your gift."
                  data-testid="donate-message-input"
                />
              </div>

              <label className="flex items-center gap-3 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#2E8B8B] focus:ring-[#2E8B8B]"
                  data-testid="donate-anonymous-checkbox"
                />
                I'd like to give anonymously.
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-70"
                data-testid="donate-submit-button"
              >
                <Heart className="h-4 w-4" />
                {submitting ? "Sending your intention…" : "Send my contribution"}
                <ArrowRight className="h-4 w-4" />
              </button>

              <p className="text-center text-xs text-slate-500">
                This form captures your intention to give. A team member will follow up
                personally to complete your contribution.
              </p>
            </form>
          </div>

          {/* SIDE */}
          <div className="lg:col-span-5 space-y-6 reveal">
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#0A2540] to-[#1E4D6B] text-white p-10 sm:p-12">
              <div className="absolute inset-0 -z-0 opacity-15 pointer-events-none" aria-hidden>
                <img src={RIPPLE} alt="" className="absolute inset-0 h-full w-full object-cover" />
              </div>
              <p className="relative text-xs uppercase tracking-[0.26em] text-[#3AABAB]">CLEAN Water Drop Association</p>
              <p className="relative font-heading mt-6 text-[22px] sm:text-2xl leading-[1.4] font-light">
                Your contribution supports shelter, food, hygiene, mental health and
                mentorship — practical, everyday services delivered with dignity.
              </p>
            </div>

            <div className="card-soft">
              <p className="eyebrow">Where your gift goes</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                {[
                  "Crisis accommodation",
                  "Food, laundry & hygiene services",
                  "Mental health & psychosocial support",
                  "Mentorship & case support",
                  "Education and employment pathways",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="mt-2 h-1 w-1 rounded-full bg-[#2E8B8B] flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                As a growing community initiative, every contribution goes directly toward
                delivering practical, trauma-informed support.
              </p>
            </div>

            <div className="card-soft">
              <ShieldCheck className="h-6 w-6 text-[#2E8B8B]" />
              <p className="font-heading mt-4 text-lg text-[#0A2540]">Community-led</p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                We follow trauma-informed, ethical fundraising principles —
                no pressure, no guilt. Questions are always welcome.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
